import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';
import {
  ArrowLeft,
  CircleNotch,
  Plus,
  Trash,
  Download,
  PaperPlaneTilt,
  CheckCircle,
  CurrencyDollar,
  PencilSimple,
  Signature,
  ClockCounterClockwise,
  ShieldCheck
} from '@phosphor-icons/react';


const PAYMENT_TERMS_OPTIONS = [
  '50% Deposit + 50% on Completion',
  '100% on Completion',
  'Custom (see notes)'
];

function QuoteStatusBadge({ status }) {
  const styles = {
    'Draft': 'badge-draft',
    'Pending Approval': 'badge-pending',
    'Approved': 'badge-approved',
    'Sent': 'badge-sent',
    'Signed': 'badge-signed',
    'Paid': 'badge-paid',
    'Expired': 'badge-expired',
  };

  const labels = {
    'Draft': 'Borrador',
    'Pending Approval': 'Pendiente Aprobación',
    'Approved': 'Aprobada',
    'Sent': 'Enviada',
    'Signed': 'Firmada',
    'Paid': 'Pagada',
    'Expired': 'Vencida',
  };

  return (
    <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${styles[status] || 'badge-draft'}`}>
      {labels[status] || status}
    </span>
  );
}

export default function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const isNew = id === 'new';
  const preselectedClientId = searchParams.get('client_id');
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';
  const isSales = currentUser?.role === 'sales';

  const [quote, setQuote] = useState({
    client_id: preselectedClientId || '',
    project_address: '',
    project_city: '',
    project_state: '',
    project_zip: '',
    items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
    tax_percent: 0,
    payment_terms: '50% Deposit + 50% on Completion',
    notes: '',
    language: 'ES'
  });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchClients();
    if (!isNew) {
      fetchQuote();
    }
  }, [id, isNew]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`, { withCredentials: true });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/quotes/${id}`, { withCredentials: true });
      setQuote(response.data);
    } catch (error) {
      console.error('Error fetching quote:', error);
      navigate('/quotes');
    } finally {
      setLoading(false);
    }
  };

  const calculateItemTotal = (item) => {
    return item.quantity * item.unit_price;
  };

  const calculateSubtotal = () => {
    return quote.items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * (quote.tax_percent / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...quote.items];
    newItems[index] = {
      ...newItems[index],
      [field]: field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value
    };
    newItems[index].total = calculateItemTotal(newItems[index]);
    setQuote({ ...quote, items: newItems });
  };

  const addItem = () => {
    setQuote({
      ...quote,
      items: [...quote.items, { description: '', quantity: 1, unit_price: 0, total: 0 }]
    });
  };

  const removeItem = (index) => {
    if (quote.items.length === 1) return;
    const newItems = quote.items.filter((_, i) => i !== index);
    setQuote({ ...quote, items: newItems });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!quote.client_id) {
      alert('Selecciona un cliente');
      return;
    }
    if (!quote.items.some(item => item.description && item.quantity > 0)) {
      alert('Agrega al menos un servicio');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...quote,
        items: quote.items.map(item => ({
          ...item,
          total: calculateItemTotal(item)
        }))
      };

      if (isNew) {
        const response = await axios.post(`${API_URL}/api/quotes`, payload, { withCredentials: true });
        navigate(`/quotes/${response.data.id}`);
      } else {
        await axios.put(`${API_URL}/api/quotes/${id}`, payload, { withCredentials: true });
        setEditing(false);
        fetchQuote();
      }
    } catch (error) {
      console.error('Error saving quote:', error);
      alert('Error al guardar la cotización');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${API_URL}/api/quotes/${id}/approve`, {}, { withCredentials: true });
      fetchQuote();
    } catch (error) {
      console.error('Error approving quote:', error);
      alert('Error al aprobar la cotizacion');
    }
  };

  const handleRequestApproval = async () => {
    try {
      await axios.post(`${API_URL}/api/quotes/${id}/request-approval`, {}, { withCredentials: true });
      alert('Solicitud de aprobacion enviada. El administrador ha sido notificado.');
      fetchQuote();
    } catch (error) {
      console.error('Error requesting approval:', error);
      alert('Error al solicitar aprobacion');
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      await axios.post(`${API_URL}/api/quotes/${id}/email`, {}, { withCredentials: true });
      alert('Cotización enviada por email exitosamente');
      fetchQuote();
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error al enviar el email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/quotes/${id}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cotizacion-${quote.quote_number || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const handleCreatePayment = async (paymentType) => {
    try {
      const amount = paymentType === 'deposit' ? calculateTotal() * 0.5 : calculateTotal();
      const response = await axios.post(`${API_URL}/api/payments/create-checkout`, {
        quote_id: id,
        amount: amount,
        payment_type: paymentType
      }, { withCredentials: true });
      
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      alert('Error al crear el link de pago');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={40} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="quote-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/quotes')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">
            {isNew ? 'Nueva Cotización' : editing ? 'Editar Cotización' : quote.quote_number}
          </h1>
          {!isNew && !editing && (
            <div className="flex items-center gap-2 mt-1">
              <QuoteStatusBadge status={quote.status} />
              {quote.client_name && (
                <span className="text-slate-500">• {quote.client_name}</span>
              )}
            </div>
          )}
        </div>
        {!isNew && !editing && (
          <div className="flex items-center gap-2 flex-wrap">
            {quote.status === 'Draft' && (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                  data-testid="edit-quote-button"
                >
                  <PencilSimple size={18} />
                  Editar
                </button>
                {isAdmin ? (
                  <button
                    onClick={handleApprove}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    data-testid="approve-button"
                  >
                    <ShieldCheck size={18} />
                    Aprobar
                  </button>
                ) : (
                  <button
                    onClick={handleRequestApproval}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                    data-testid="request-approval-button"
                  >
                    <ClockCounterClockwise size={18} />
                    Solicitar Aprobacion
                  </button>
                )}
              </>
            )}
            {quote.status === 'Pending Approval' && (
              <>
                {isAdmin ? (
                  <button
                    onClick={handleApprove}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    data-testid="approve-button"
                  >
                    <ShieldCheck size={18} />
                    Aprobar Cotizacion
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-lg text-sm font-medium">
                    <ClockCounterClockwise size={18} />
                    Esperando aprobacion del Admin
                  </span>
                )}
              </>
            )}
            {quote.status === 'Approved' && (
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                data-testid="send-email-button"
              >
                {sendingEmail ? <CircleNotch size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} />}
                {sendingEmail ? 'Enviando...' : 'Enviar al Cliente'}
              </button>
            )}
            {['Approved', 'Sent', 'Signed'].includes(quote.status) && (
              <div className="relative group">
                <button
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  data-testid="payment-button"
                >
                  <CurrencyDollar size={18} />
                  Generar Pago
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                  <button
                    onClick={() => handleCreatePayment('deposit')}
                    className="w-full px-4 py-2 text-left hover:bg-slate-50 rounded-t-lg"
                    data-testid="payment-deposit"
                  >
                    Depósito (50%)
                  </button>
                  <button
                    onClick={() => handleCreatePayment('full')}
                    className="w-full px-4 py-2 text-left hover:bg-slate-50 rounded-b-lg"
                    data-testid="payment-full"
                  >
                    Pago Total
                  </button>
                </div>
              </div>
            )}
            <button
              onClick={handleDownloadPdf}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
              data-testid="download-pdf-button"
            >
              <Download size={18} />
              PDF
            </button>
          </div>
        )}
      </div>

      {editing ? (
        /* Edit Form */
        <form onSubmit={handleSave} className="space-y-6">
          {/* Client Selection */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Cliente</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Seleccionar Cliente *</label>
                <select
                  value={quote.client_id}
                  onChange={(e) => setQuote({...quote, client_id: e.target.value})}
                  className="form-input"
                  required
                  data-testid="client-select"
                >
                  <option value="">Seleccionar...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name} {client.company ? `(${client.company})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Idioma</label>
                <select
                  value={quote.language}
                  onChange={(e) => setQuote({...quote, language: e.target.value})}
                  className="form-input"
                  data-testid="language-select"
                >
                  <option value="ES">Español</option>
                  <option value="EN">English</option>
                </select>
              </div>
            </div>
          </div>

          {/* Project Location */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Ubicación del Proyecto</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="form-label">Dirección</label>
                <input
                  type="text"
                  value={quote.project_address || ''}
                  onChange={(e) => setQuote({...quote, project_address: e.target.value})}
                  className="form-input"
                  data-testid="project-address-input"
                />
              </div>
              <div>
                <label className="form-label">Ciudad</label>
                <input
                  type="text"
                  value={quote.project_city || ''}
                  onChange={(e) => setQuote({...quote, project_city: e.target.value})}
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">Estado</label>
                <input
                  type="text"
                  value={quote.project_state || ''}
                  onChange={(e) => setQuote({...quote, project_state: e.target.value})}
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">Código Postal</label>
                <input
                  type="text"
                  value={quote.project_zip || ''}
                  onChange={(e) => setQuote({...quote, project_zip: e.target.value})}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Services/Items */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Servicios</h2>
              <button
                type="button"
                onClick={addItem}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                data-testid="add-item-button"
              >
                <Plus size={16} />
                Agregar
              </button>
            </div>
            
            <div className="space-y-4">
              {quote.items.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-3 items-end p-4 bg-slate-50 rounded-lg">
                  <div className="col-span-12 md:col-span-5">
                    <label className="form-label">Descripción</label>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      className="form-input"
                      placeholder="Descripción del servicio"
                      data-testid={`item-description-${index}`}
                    />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <label className="form-label">Cantidad</label>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      className="form-input"
                      min="1"
                      step="0.01"
                      data-testid={`item-quantity-${index}`}
                    />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <label className="form-label">Precio Unit.</label>
                    <input
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      data-testid={`item-price-${index}`}
                    />
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <label className="form-label">Total</label>
                    <p className="form-input bg-slate-100">
                      ${calculateItemTotal(item).toLocaleString()}
                    </p>
                  </div>
                  <div className="col-span-1">
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      disabled={quote.items.length === 1}
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Subtotal:</span>
                    <span className="font-medium">${calculateSubtotal().toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600">Tax (%):</span>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={quote.tax_percent === 0}
                          onChange={(e) => setQuote({...quote, tax_percent: e.target.checked ? 0 : 7})}
                          className="rounded border-slate-300"
                          data-testid="tax-exempt-checkbox"
                        />
                        <span className="text-xs text-slate-500">Exento</span>
                      </label>
                    </div>
                    <input
                      type="number"
                      value={quote.tax_percent}
                      onChange={(e) => setQuote({...quote, tax_percent: parseFloat(e.target.value) || 0})}
                      className="w-20 px-2 py-1 border border-slate-200 rounded text-right"
                      min="0"
                      step="0.01"
                      disabled={quote.tax_percent === 0 && false}
                      data-testid="tax-input"
                    />
                  </div>
                  {quote.tax_percent === 0 && (
                    <p className="text-xs text-amber-600 text-right">Sin impuestos (Tax Exempt)</p>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>Total:</span>
                    <span className="text-blue-600">${calculateTotal().toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Terms & Notes */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Términos de Pago</label>
                <select
                  value={quote.payment_terms}
                  onChange={(e) => setQuote({...quote, payment_terms: e.target.value})}
                  className="form-input"
                  data-testid="payment-terms-select"
                >
                  {PAYMENT_TERMS_OPTIONS.map(term => (
                    <option key={term} value={term}>{term}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Notas adicionales</label>
                <textarea
                  value={quote.notes || ''}
                  onChange={(e) => setQuote({...quote, notes: e.target.value})}
                  className="form-input"
                  rows={3}
                  data-testid="notes-input"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => isNew ? navigate('/quotes') : setEditing(false)}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
              data-testid="cancel-button"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              data-testid="save-button"
            >
              {saving && <CircleNotch size={18} className="animate-spin" />}
              {saving ? 'Guardando...' : 'Guardar Cotización'}
            </button>
          </div>
        </form>
      ) : (
        /* View Mode */
        <div className="space-y-6">
          {/* Workflow Progress */}
          {!['Paid', 'Expired'].includes(quote.status) && (
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="workflow-progress">
              <p className="text-xs font-medium text-slate-500 mb-3">FLUJO DE LA COTIZACION</p>
              <div className="flex items-center gap-1 overflow-x-auto">
                {[
                  { key: 'Draft', label: 'Borrador' },
                  { key: 'Pending Approval', label: 'Pendiente' },
                  { key: 'Approved', label: 'Aprobada' },
                  { key: 'Sent', label: 'Enviada' },
                  { key: 'Signed', label: 'Firmada' },
                  { key: 'Paid', label: 'Pagada' },
                ].map((step, i, arr) => {
                  const statusOrder = ['Draft', 'Pending Approval', 'Approved', 'Sent', 'Signed', 'Paid'];
                  const currentIdx = statusOrder.indexOf(quote.status);
                  const stepIdx = statusOrder.indexOf(step.key);
                  const isActive = quote.status === step.key;
                  const isPast = stepIdx < currentIdx;
                  return (
                    <React.Fragment key={step.key}>
                      <div className={`flex-1 min-w-[70px] text-center py-1.5 px-1 rounded text-xs font-semibold transition-colors ${
                        isActive ? 'bg-blue-600 text-white' : isPast ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'
                      }`}>
                        {step.label}
                      </div>
                      {i < arr.length - 1 && (
                        <div className={`w-4 h-0.5 shrink-0 ${isPast ? 'bg-green-400' : 'bg-slate-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quote Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-slate-500">Cliente</p>
                <Link to={`/clients/${quote.client_id}`} className="font-medium text-blue-600 hover:text-blue-700">
                  {quote.client_name}
                </Link>
                <p className="text-sm text-slate-500">{quote.client_email}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Fecha de Creación</p>
                <p className="font-medium">
                  {new Date(quote.created_at).toLocaleDateString('es-ES', {
                    year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Válida hasta</p>
                <p className="font-medium">
                  {new Date(quote.expires_at).toLocaleDateString('es-ES', {
                    year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </p>
              </div>
              {quote.project_address && (
                <div className="md:col-span-3">
                  <p className="text-sm text-slate-500">Ubicación del Proyecto</p>
                  <p className="font-medium">
                    {quote.project_address}
                    {quote.project_city && `, ${quote.project_city}`}
                    {quote.project_state && `, ${quote.project_state}`}
                    {quote.project_zip && ` ${quote.project_zip}`}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold">Servicios</h2>
            </div>
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Descripción</th>
                  <th className="px-6 py-3 text-center text-xs font-bold uppercase text-slate-500">Cantidad</th>
                  <th className="px-6 py-3 text-right text-xs font-bold uppercase text-slate-500">Precio Unit.</th>
                  <th className="px-6 py-3 text-right text-xs font-bold uppercase text-slate-500">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {quote.items?.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4">{item.description}</td>
                    <td className="px-6 py-4 text-center">{item.quantity}</td>
                    <td className="px-6 py-4 text-right">${item.unit_price?.toLocaleString()}</td>
                    <td className="px-6 py-4 text-right font-medium">${item.total?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50">
                <tr>
                  <td colSpan="3" className="px-6 py-3 text-right text-sm text-slate-600">Subtotal:</td>
                  <td className="px-6 py-3 text-right font-medium">${quote.subtotal?.toLocaleString()}</td>
                </tr>
                <tr>
                  <td colSpan="3" className="px-6 py-3 text-right text-sm text-slate-600">
                    Tax ({quote.tax_percent}%){quote.tax_percent === 0 && <span className="ml-1 text-amber-600 text-xs">(Exento)</span>}:
                  </td>
                  <td className="px-6 py-3 text-right font-medium">${quote.tax_amount?.toLocaleString()}</td>
                </tr>
                <tr className="bg-blue-50">
                  <td colSpan="3" className="px-6 py-4 text-right text-lg font-bold">Total:</td>
                  <td className="px-6 py-4 text-right text-lg font-bold text-blue-600">${quote.total?.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Approval Info Banner - Only show during approval workflow */}
          {quote.status === 'Pending Approval' && (
            <div className="rounded-xl border p-4 bg-amber-50 border-amber-200" data-testid="approval-info">
              <div className="flex items-center gap-3">
                <ClockCounterClockwise size={24} className="text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-800">Pendiente de Aprobacion</p>
                  <p className="text-sm text-amber-600">
                    Solicitado por {quote.approval_requested_by || 'N/A'}
                    {quote.approval_requested_at && ` - ${new Date(quote.approval_requested_at).toLocaleDateString('es-ES')}`}
                  </p>
                </div>
              </div>
            </div>
          )}
          {quote.status === 'Approved' && quote.approved_by && (
            <div className="rounded-xl border p-4 bg-green-50 border-green-200" data-testid="approval-info">
              <div className="flex items-center gap-3">
                <ShieldCheck size={24} className="text-green-600" />
                <div>
                  <p className="font-semibold text-green-800">Aprobada por {quote.approved_by}</p>
                  {quote.approved_at && (
                    <p className="text-sm text-green-600">
                      {new Date(quote.approved_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  )}
                  <p className="text-xs text-green-600 mt-1">Lista para enviar al cliente</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold mb-3">Términos de Pago</h3>
              <p className="text-slate-600">{quote.payment_terms}</p>
            </div>
            {quote.notes && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold mb-3">Notas</h3>
                <p className="text-slate-600 whitespace-pre-wrap">{quote.notes}</p>
              </div>
            )}
          </div>

          {/* Payment Info */}
          {quote.amount_paid > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <h3 className="font-semibold text-green-800 mb-2">Información de Pago</h3>
              <div className="flex justify-between items-center">
                <span className="text-green-700">Monto pagado:</span>
                <span className="text-xl font-bold text-green-800">${quote.amount_paid?.toLocaleString()}</span>
              </div>
              {quote.payment_status === 'Partial' && (
                <p className="text-sm text-green-600 mt-2">
                  Balance pendiente: ${(quote.total - quote.amount_paid).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {/* Signature Info */}
          {quote.signed_at && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6" data-testid="signature-info">
              <div className="flex items-center gap-3">
                <Signature size={24} className="text-green-600" />
                <div>
                  <h3 className="font-semibold text-green-800">Cotizacion Firmada Digitalmente</h3>
                  <p className="text-green-700">
                    Firmado por: <strong>{quote.signature_name}</strong>
                  </p>
                  <p className="text-green-600 text-sm">
                    Fecha: {new Date(quote.signed_at).toLocaleString('es-ES')}
                  </p>
                </div>
              </div>
              {/* Auto Flow Info */}
              {quote.auto_flow_processed && (
                <div className="mt-4 pt-4 border-t border-green-200 space-y-2">
                  {quote.auto_invoice_number && (
                    <p className="text-sm text-green-700">
                      Factura auto-generada: <strong>{quote.auto_invoice_number}</strong>
                    </p>
                  )}
                  {quote.auto_deposit_amount && (
                    <p className="text-sm text-green-700">
                      Anticipo (50%): <strong>${quote.auto_deposit_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong>
                      {quote.auto_payment_checkout_url && (
                        <a href={quote.auto_payment_checkout_url} target="_blank" rel="noopener noreferrer"
                          className="ml-2 text-blue-600 underline text-xs"
                        >Ver link de pago</a>
                      )}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Signing Link (for Sent quotes) */}
          {quote.status === 'Sent' && !quote.signed_at && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6" data-testid="signing-link-info">
              <div className="flex items-center gap-3 mb-3">
                <Signature size={24} className="text-amber-600" />
                <h3 className="font-semibold text-amber-800">Link de Firma Digital</h3>
              </div>
              <p className="text-sm text-amber-700 mb-3">
                El cliente puede firmar desde este link (incluido en el email):
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={`${window.location.origin}/quote/${quote.id}`}
                  className="flex-1 px-3 py-2 bg-white border border-amber-300 rounded-lg text-sm text-slate-700"
                  data-testid="signing-link-url"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/quote/${quote.id}`);
                    alert('Link de firma copiado');
                  }}
                  className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
                  data-testid="copy-signing-link-btn"
                >
                  Copiar
                </button>
              </div>
            </div>
          )}

          {/* Admin: Test Email Flow */}
          {isAdmin && (
            <TestEmailFlowSection quoteId={quote.id} />
          )}
        </div>
      )}
    </div>
  );
}

function TestEmailFlowSection({ quoteId }) {
    const [emails, setEmails] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  const handleTestFlow = async () => {
    const emailList = emails.split(',').map(e => e.trim()).filter(Boolean);
    if (emailList.length === 0) {
      alert('Ingresa al menos un correo');
      return;
    }
    setSending(true);
    setResults(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/quotes/${quoteId}/test-email-flow`,
        { emails: emailList },
        { withCredentials: true }
      );
      setResults(res.data.results);
    } catch (err) {
      alert('Error al enviar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-6" data-testid="test-email-flow">
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        <PaperPlaneTilt size={20} className="text-slate-600" />
        Prueba de Flujo de Emails
      </h3>
      <p className="text-sm text-slate-500 mb-3">
        <strong className="text-red-600">SOLO PARA ADMIN:</strong> Envia una prueba del flujo completo de 12 pasos a los correos indicados. No usar correos de clientes reales.
      </p>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          placeholder="correo1@ejemplo.com, correo2@ejemplo.com"
          className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
          data-testid="test-email-input"
        />
        <button
          onClick={handleTestFlow}
          disabled={sending}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          data-testid="test-email-send-btn"
        >
          {sending ? 'Enviando...' : 'Enviar Prueba'}
        </button>
      </div>
      {results && (
        <div className="space-y-1 mt-3">
          {results.map((r, i) => (
            <div key={i} className={`text-sm flex items-center gap-2 ${r.status === 'sent' ? 'text-green-600' : 'text-red-600'}`}>
              {r.status === 'sent' ? <CheckCircle size={16} /> : '!'}
              <span className="font-medium">{r.step.replace('_', ' ')}</span>
              <span>- {r.status === 'sent' ? 'Enviado' : r.error}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
